import api from '../api';
import type { CccdParameterType } from '../cccdScoringParameters';

// Parámetro de evaluación — recurso GLOBAL (cccd-scoring-global-parameters):
// se define una sola vez en /cccd/scoring-parameters y se vincula
// directamente a categorías de cualquier evento o regional, nunca se
// recrea ni se clona por evento.
export interface EvaluationParameter {
  id: string;
  name: string;
  parameterType: CccdParameterType;
  unitOfMeasure: string | null;
  maxScore: number | null;
  targetHeight: number | null;
  maxError: number | null;
  perfectBand: number | null;
  minTime: number | null;
  maxTime: number | null;
  tolerance: number | null;
  maxValue: number | null;
  createdAt: string;
  updatedAt: string;
}

export type EvaluationParameterInput = Omit<EvaluationParameter, 'id' | 'createdAt' | 'updatedAt'>;

export interface CategoryEvaluationParameter extends EvaluationParameter {
  orderIndex: number;
}

export interface CccdTeamResult {
  id: string;
  teamId: string;
  parameterId: string;
  rawValue: number | null;
  calculatedScore: number | null;
  angleR1: number | null;
  angleR2: number | null;
  angleR3: number | null;
  adjustmentA1: number | null;
  adjustmentA2: number | null;
  adjustmentA3: number | null;
  heightH1: number | null;
  heightH2: number | null;
  heightH3: number | null;
  targetHeightH0: number | null;
  parameter: EvaluationParameter;
}

export interface SaveTeamResultInput {
  parameterId: string;
  rawValue?: number | null;
  angleR1?: number | null;
  angleR2?: number | null;
  angleR3?: number | null;
  adjustmentA1?: number | null;
  adjustmentA2?: number | null;
  adjustmentA3?: number | null;
  targetHeightH0?: number | null;
}

export interface TeamMemberRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  documentType: string | null;
  documentNumber: string | null;
  role: string;
  inviteStatus: string;
}

export interface CategoryRankingRow {
  teamId: string;
  position: number;
}

// Vista de ranking independiente (cccd-scoring-leaderboard) — no confundir
// con getCategoryRanking, que solo trae {teamId, position} y también
// alimenta el polling público de "puntos"; este endpoint es exclusivo de
// Gestión y trae el desglose por parámetro para la pestaña Clasificación.
export interface CategoryLeaderboardParameter {
  id: string;
  name: string;
}

export interface CategoryLeaderboardTeam {
  teamId: string;
  name: string;
  institution: { name: string } | null;
  totalScore: number;
  position: number | null;
  scores: { parameterId: string; rawValue: number | null; calculatedScore: number | null }[];
}

export interface CategoryLeaderboard {
  parameters: CategoryLeaderboardParameter[];
  teams: CategoryLeaderboardTeam[];
}

// Grilla de captura tipo hoja de cálculo (cccd-scoring-spreadsheet-grid) —
// una fila por equipo, con el rawValue de cada parámetro vinculado a la
// categoría (null si no se ha registrado). Distinto de CccdTeamResult: no
// trae los campos auxiliares (ángulos, ajustes, targetHeightH0), esos se
// consultan aparte (getTeamResults) solo al abrir el popover de
// calculated_height.
export interface CategoryResultsGridTeam {
  id: string;
  name: string;
  institution: { name: string } | null;
  position: number | null;
  results: Record<string, number | null>;
}

export interface CategoryResultsGrid {
  parameters: CategoryEvaluationParameter[];
  teams: CategoryResultsGridTeam[];
}

export type CccdLaunchStatus =
  | 'sin_estado'
  | 'por_programar'
  | 'programado'
  | 'proximo_a_lanzar'
  | 'en_lanzamiento'
  | 'lanzado'
  | 'no_volo'
  | 'en_revision';

export interface LaunchOrderTeam {
  id: string;
  name: string;
  launchStatus: CccdLaunchStatus;
  launchOrder: number | null;
  institution: { name: string } | null;
}

export interface LiveLaunchControlState {
  liveCategory: { id: string; name: string; slug: string; imageUrl: string | null };
  teams: LaunchOrderTeam[];
}

function data<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

export const cccdScoring = {
  // Evaluation parameters (biblioteca global — /cccd/scoring-parameters)
  listParameters: () => api.get<{ data: EvaluationParameter[] }>('/cccd/scoring/parameters').then(data),

  createParameter: (body: EvaluationParameterInput) =>
    api.post<{ data: EvaluationParameter }>('/cccd/scoring/parameters', body).then(data),

  updateParameter: (id: string, body: Partial<EvaluationParameterInput>) =>
    api.patch<{ data: EvaluationParameter }>(`/cccd/scoring/parameters/${id}`, body).then(data),

  deleteParameter: (id: string) => api.delete(`/cccd/scoring/parameters/${id}`),

  // Category <-> parameter linking (categoryId puede ser de un evento
  // nacional o regional — el vínculo es genérico)
  listCategoryParameters: (categoryId: string) =>
    api.get<{ data: CategoryEvaluationParameter[] }>(`/cccd/scoring/categories/${categoryId}/parameters`).then(data),

  linkCategoryParameter: (categoryId: string, parameterId: string, orderIndex: number) =>
    api.post<{ data: { id: string } }>(`/cccd/scoring/categories/${categoryId}/parameters`, { parameterId, orderIndex }).then(data),

  reorderCategoryParameter: (categoryId: string, parameterId: string, orderIndex: number) =>
    api.patch<{ data: { id: string } }>(`/cccd/scoring/categories/${categoryId}/parameters/${parameterId}`, { orderIndex }).then(data),

  unlinkCategoryParameter: (categoryId: string, parameterId: string) =>
    api.delete(`/cccd/scoring/categories/${categoryId}/parameters/${parameterId}`),

  // Results
  getTeamResults: (teamId: string) =>
    api
      .get<{ data: { results: CccdTeamResult[]; evaluatedPercentage: number } }>(`/cccd/scoring/teams/${teamId}/results`)
      .then(data),

  saveTeamResult: (teamId: string, body: SaveTeamResultInput) =>
    api.put<{ data: { ranking: CategoryRankingRow[] } }>(`/cccd/scoring/teams/${teamId}/results`, body).then(data),

  getTeamMembers: (teamId: string) =>
    api.get<{ data: TeamMemberRow[] }>(`/cccd/scoring/teams/${teamId}/members`).then(data),

  getCategoryRanking: (categoryId: string) =>
    api.get<{ data: CategoryRankingRow[] }>(`/cccd/scoring/categories/${categoryId}/ranking`).then(data),

  getCategoryLeaderboard: (categoryId: string) =>
    api.get<{ data: CategoryLeaderboard }>(`/cccd/scoring/categories/${categoryId}/leaderboard`).then(data),

  getCategoryResultsGrid: (categoryId: string) =>
    api.get<{ data: CategoryResultsGrid }>(`/cccd/scoring/categories/${categoryId}/results-grid`).then(data),

  // Launch control (categoryId genérico — nacional o regional)
  setTeamLaunchStatus: (teamId: string, launchStatus: CccdLaunchStatus) =>
    api.put<{ data: { id: string; launchStatus: CccdLaunchStatus } }>(`/cccd/launch-control/teams/${teamId}/status`, { launchStatus }).then(data),

  getCategoryLaunchOrder: (categoryId: string) =>
    api
      .get<{ data: LaunchOrderTeam[] }>(`/cccd/launch-control/categories/${categoryId}/order`)
      .then(data),

  setCategoryLaunchOrder: (categoryId: string, order: { teamId: string; launchOrder: number }[]) =>
    api.put<{ data: null }>(`/cccd/launch-control/categories/${categoryId}/order`, { order }).then(data),

  setCategoryLive: (categoryId: string, isLive: boolean) =>
    api.put<{ data: { id: string; isLive: boolean } }>(`/cccd/launch-control/categories/${categoryId}/live`, { isLive }).then(data),
};
